/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISitePickerViewProps } from '@msdyn365-commerce-modules/site-picker';

import { ISiteOptionsData, ISitePickerConfig } from '../../master/definition-extensions/site-picker.ext.props.autogenerated';

interface IExtendedSitePickerViewProps extends Omit<ISitePickerViewProps, 'config'> {
    store: IModuleProps;
    config: ISitePickerConfig;
}

export default (props: IExtendedSitePickerViewProps) => {
    return <>{_renderSitePicker(props)}</>;
};

const _renderDropdownView = (props: IExtendedSitePickerViewProps, currentLanguage?: ISiteOptionsData, showShortSiteName?: boolean) => {
    const { SitePickerMenu, SitePickerMenuSpan } = props;
    //show EN fallback if curentLanguage.shortSiteName is undefined or empty - case if / is the current site path
    return (
        <>
            <Node {...SitePickerMenu} onClick={props.renderSiteOptions}>
                {showShortSiteName ? currentLanguage?.shortSiteName || 'EN' : currentLanguage?.siteName}
                <Node {...SitePickerMenuSpan} />
            </Node>
            {props.siteOptionsState ? _renderSiteOptionsList(props) : null}
        </>
    );
};

const _renderSiteOptionsList = (props: IExtendedSitePickerViewProps): React.ReactNode => {
    const { SitePickerMenuList, SitePickerMenuListItem, SitePickerMenuListItemArray } = props;
    const contextUrlPath = props.context.request.url.requestUrl.pathname;

    const options = SitePickerMenuListItemArray!
        .filter(item => {
            if (!React.isValidElement(item)) {
                return true;
            }
            const itemUrl = item.props?.href;

            const normalizedItemUrl = (itemUrl?.replace(/\/$/, '') || '').toLowerCase();
            const normalizedContextPath = contextUrlPath.replace(/\/$/, '').toLowerCase();
            const pathSegments = normalizedContextPath.split('/');

            const languageBasePath = pathSegments.slice(0, 2).join('/');

            return !(normalizedItemUrl === languageBasePath || (normalizedItemUrl === '' && props.context.request.locale === 'en-GB'));
        })
        .map((item, index) => (
            <Node {...SitePickerMenuListItem} key={index}>
                {item}
            </Node>
        ));

    return (
        <Node {...SitePickerMenuList} onMouseLeave={props.resetSiteOptionsState}>
            {options}
        </Node>
    );
};

const _renderSitePicker = (props: IExtendedSitePickerViewProps): React.ReactNode => {
    // Extends the original site picker behavior
    // Checks for current site path value and evaluates set language
    const { sitePath } = props.store.requestContext;
    const { siteOptions, showShortSiteNameLargeScreen } = props.config;

    const defaultLanguage: ISiteOptionsData = {
        siteRedirectUrl: { destinationUrl: sitePath },
        shortSiteName: 'EN',
        siteName: 'Global (English)'
    };

    const findDefaultLanguage = (siteOptions?.find(o => o.shortSiteName === 'EN') ?? defaultLanguage) as ISiteOptionsData;

    const currentLanguage: ISiteOptionsData =
        siteOptions?.find(
            (option): option is ISiteOptionsData =>
                (option?.siteRedirectUrl?.destinationUrl || '').toLowerCase() === (sitePath || '').toLowerCase()
        ) ?? findDefaultLanguage;

    const { isMobileView, SitePicker } = props;
    const showShortSiteName = showShortSiteNameLargeScreen || isMobileView;

    return <Module {...SitePicker}>{_renderDropdownView(props, currentLanguage, showShortSiteName)}</Module>;
};
